import { useState } from 'react';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';

/**
 * Confirmation dialog shown when the user opts a phase into its own
 * canvas snapshot. The action is irreversible — once seeded, drags in
 * this phase no longer update the project-wide template, and there is
 * no UI to merge the override back into the template.
 */
export function SeedPhaseDialog({
  open,
  phaseName,
  taskCount,
  onConfirm,
  onClose,
}: {
  open: boolean;
  phaseName: string;
  taskCount: number;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);

  return (
    <Dialog
      open={open}
      onClose={busy ? () => undefined : onClose}
      title="このフェーズの編集を開始する"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            キャンセル
          </Button>
          <Button
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
          >
            {busy ? '切替中…' : '編集モードにする'}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-slate-700">
        <p>
          フェーズ「<span className="font-semibold">{phaseName}</span>
          」を編集モードに切り替えます ({taskCount} タスクの現在位置を
          スナップショットします)。
        </p>
        <ul className="space-y-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <li>
            • 切り替え後、このフェーズで動かしたタスク配置は <strong>このフェーズだけ</strong>
            に反映され、他のフェーズには伝播しません
          </li>
          <li>
            • 一度切り替えた後、共通配置に戻す手段はありません
          </li>
          <li>
            • 完了済みフェーズになると配置は閲覧のみ・編集不可になります
          </li>
        </ul>
        <p className="text-xs text-slate-500">
          進める前に、現在の配置がこのフェーズの開始時点としてふさわしいことを確認してください。
        </p>
      </div>
    </Dialog>
  );
}
